<template>
  <div>

    <v-app-bar height="50" :flat="!scrolled" color="transparent" :class="['blur-menu', 'menu-style', 'px-4', isDarkMode && 'blur-menu--dark']">

      <v-spacer></v-spacer>

      <!-- Menu pro desktop -->


      <v-btn @click="navigateTo('/')" variant="text" :color="isRouteActive('/') ? undefined : menuColor" class="d-none  d-md-flex menu-btn" :class="{ 'menu-btn--active': isRouteActive('/') }">{{ $t('nav.events') }}</v-btn><span
        :class="['menu-separator', 'd-none', 'd-md-flex', isDarkMode && 'menu-separator--dark']">|</span>
      <v-btn @click="navigateTo('/teams')" variant="text" :color="isRouteActive('/teams') ? undefined : menuColor" class="d-none d-md-flex menu-btn" :class="{ 'menu-btn--active': isRouteActive('/teams') }">{{ $t('nav.teams') }}</v-btn><span
        :class="['menu-separator', 'd-none', 'd-md-flex', isDarkMode && 'menu-separator--dark']">|</span>
      <v-btn @click="navigateTo('/leagues')" variant="text" :color="isRouteActive('/leagues') ? undefined : menuColor"
        class="d-none  d-md-flex menu-btn" :class="{ 'menu-btn--active': isRouteActive('/leagues') }">{{ $t('nav.organizations') }}</v-btn>

      <v-spacer></v-spacer>

    </v-app-bar>





  

    

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" location="top" color="black" timeout="3000">
      <v-icon>mdi-content-copy</v-icon> {{ $t('nav.invitationCopied') }}
    </v-snackbar>

 
  <!-- Bug Report Dialog -->
  <BugReportDialog v-model:show="showBugReportDialog" />

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useUserStore } from '@/stores/user/userStore'; // Import user store
import { useMessageStore } from '@/stores/message/messageStore';
import { useChatStore } from '@/stores/chat/chatStore';
import { useAppStore } from '@/stores/app/appStore'; // Import app store
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import axiosInstance from '@/services/axiosInstance';
import LogoPath from '@/assets/img/logos/hockeybook-path.svg';
import LogoAngle from '@/assets/img/logos/logo-angle.svg';
import { useDisplay, useTheme } from 'vuetify';
import { canAddEvents } from '@/utils/userUtils'
import AddEventDialog from '@/components/AddEventDialog.vue'
import BugReportDialog from '@/components/BugReportDialog.vue'
import { getUserAvatarUrl } from '@/utils/imageUtils';
import { BUILD_VERSION } from '@/version';
import { useNavTransition } from '@/composables/useNavTransition';


const BRAND = import.meta.env.VITE_BRAND as string

const { mdAndUp } = useDisplay();
const theme = useTheme();
const { setTab, isMainRoute } = useNavTransition();

const menuItems = [
  { path: '/' },
  { path: '/teams' },
  { path: '/leagues' },
  { path: '/messages' },
]

function navigateTo(path: string) {
  const targetIndex = menuItems.findIndex(item => item.path === path)
  if (targetIndex < 0) return

  if (isMainRoute(route.path)) {
    setTab(targetIndex)
    router.replace(path).catch(() => {})
  } else {
    router.push(path).catch(() => {})
  }
}

// Theme toggle
const isDarkMode = computed(() => theme.global.current.value.dark);
const menuColor = computed(() => isDarkMode.value ? 'white' : 'black');

function isRouteActive(path: string): boolean {
  const current = route.path
  if (path === '/') return current === '/' || current === '/events'
  return current.startsWith(path)
}

const toggleTheme = () => {
  theme.global.name.value = isDarkMode.value ? 'light' : 'customDarkTheme';
  // Save preference to localStorage
  localStorage.setItem('theme', theme.global.name.value);
};

// Load theme preference on mount
onMounted(() => {
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme && (savedTheme === 'light' || savedTheme === 'customDarkTheme')) {
    theme.global.name.value = savedTheme;
  }
});


const showAddEventDialog = ref(false)
const showBugReportDialog = ref(false)
const selectedDate = ref<Date | null>(null);
const canAddEventAllowed = ref(false);

// desktop (md+) => hockeybook-path, mobile => logo-angle
const logoSrc = computed(() => (mdAndUp.value ? LogoAngle : LogoPath));

const scrolled = ref(false);
const scrollY = ref(0);


const showSnackbar = ref(false);

const route = useRoute();

const handleScroll = () => {
  scrolled.value = window.scrollY > 10;
  scrollY.value = window.scrollY;
};

// Dynamický styl pro drawer - začíná pod MainMenu (a bannerem pokud je vidět)
const drawerStyle = computed(() => {
  const isBannerRoute = userStore.isAuthenticated && ['/', '/teams', '/leagues'].includes(route.path);
  const bannerHeight = isBannerRoute ? 76 : 0;
  const menuHeight = 50;
  
  // Pokud je banner viditelný (scroll < bannerHeight), drawer začíná pod banner+menu
  // Pokud je banner odscrollovaný, menu je fixed nahoře, drawer začíná pod ním
  let topOffset: number;
  
  if (!isBannerRoute || scrollY.value >= bannerHeight) {
    // Banner je odscrollovaný nebo není na routě, menu je fixed nahoře
    topOffset = menuHeight;
  } else {
    // Banner je vidět (celý nebo částečně)
    // Drawer začíná pod viditelnou částí banneru + menu
    topOffset = bannerHeight + menuHeight - scrollY.value;
  }
  
  return {
    top: `${topOffset}px`,
    height: `calc(100% - ${topOffset}px)`,
    zIndex: 2000
  };
});



// Funkce pro přidání nové události
const handleNewEvent = async () => {
  appStore.triggerRefetch();
};

const createEvent = () => {
  selectedDate.value = new Date();
  showAddEventDialog.value = true;
};

onMounted(async () => {
  const res = await canAddEvents();
  canAddEventAllowed.value = res.allowed;
  window.addEventListener('scroll', handleScroll);
  if (userStore.isAuthenticated) {
    messageStore.fetchUnreadCount();
  }
});
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});




// Přístup k routeru
const router = useRouter();

// Stav pro boční menu (drawer)
const drawer = ref(false);

const menuTitles = ref<{ _id: string; menuTitle: string; urlTitle: string }[]>([]);
// Přístup do user store
const userStore = useUserStore();
const messageStore = useMessageStore();
const chatStore = useChatStore();
const appStore = useAppStore();

const chatUnreadCount = computed(() => chatStore.totalUnreadCount);

// Kontrola, zda přihlášený uživatel má roli coach nebo administrator
const canSeeUsers = computed(() => {
  const roles = userStore.user?.userRoles || []
  return roles.includes('administrator')
})


const userId = computed(() => userStore.getUserId);


watch(userId, async (newVal) => {
  const res = await canAddEvents();
  canAddEventAllowed.value = res.allowed;
});


// ⬇️ NOVÉ: přesměrování na "/" s návratovým parametrem
const goToLogin = () => {
  drawer.value = false; // Zavře drawer před přesměrováním
  // zachová celou aktuální cestu včetně query/hash
  const returnTo = route.fullPath || `/events/${route.params.id}`;
  router.push({ path: '/', query: { returnTo } });
};



const createInvitation = async () => {
  try {
    // Sestavení URL s refererem
    const invitationUrl = `${window.location.origin}/register?referent=${userStore.getUserId}`;

    // Zkopírování do schránky
    await navigator.clipboard.writeText(invitationUrl);

    // Zobrazení Snackbaru
    showSnackbar.value = true;
  } catch (error) {
    console.error('Nepodařilo se zkopírovat pozvánku:', error);
  }
};


const toggleDrawer = () => {
  drawer.value = !drawer.value
}


const openMyProfile = () => {
  ////console.log('Otevřít můj profil');

  // Zajištění, že `userStore.user` existuje a obsahuje požadované hodnoty
  if (!userStore.user || !userStore.user._id || !userStore.user.searchName) {
    console.error('Uživatelská data nejsou kompletní');
    return;
  }

  const searchNameArr = userStore.user.searchName.split(' ');

  // Zajištění, že `userStore.user` existuje a obsahuje požadované hodnoty
  if (searchNameArr.length < 2) {
    console.error('searchNameArr data nejsou kompletní');
    return;
  }
  ////console.log('searchNameArr:', searchNameArr);

  const myProfileRoute = `/users/${userStore.user._id}-${searchNameArr[0]}-${searchNameArr[1]}`;

  ////console.log('Otevřít můj profil:', myProfileRoute);

  // Odeslání uživatele na stránku profilu
  router.push(myProfileRoute);
};

const navigateToArticle = (urlTitle: string) => {
  router.push(`/articles/${urlTitle}`);
};

const openFacebookProfile = () => {
  ////console.log('Otevřít Facebook profil');
  window.open('https://www.facebook.com/www.modernhockey.eu', '_blank');
};

const openInstagramProfile = () => {
  ////console.log('Otevřít Facebook profil');
  window.open('https://www.instagram.com/modernhockey.eu/', '_blank');
};

const openYoutubeProfile = () => {
  ////console.log('Otevřít Facebook profil');
  window.open('https://www.youtube.com/@Modernhockeyeu', '_blank');
};


// Výpočet iniciál uživatele
const userInitials = computed(() => {
  if (!userStore.user) return '';
  const names = userStore.user.name.split(' ');
  return names.map(n => n[0]).join('').toUpperCase();
});

const userAvatar = computed(() => {
  if (!userStore.user) return null;
  return getUserAvatarUrl(userStore.user);
});

// Funkce pro odhlášení
const logout = async () => {
  await userStore.clearUser()
  window.location.href = '/login'
}



onMounted(async () => {
  try {
    const response = await axiosInstance.get(`/articles/menutitles`);
    menuTitles.value = response.data; // Načtení položek do `menuTitles`
  } catch (error) {
    console.error('Chyba při načítání menu položek:', error);
  }
});



</script>

<style scoped>
.fb-icon {
  border-radius: 25px;
}


.menu-style {
  position: sticky;
  top: 0;
  z-index: 100;
  top: 0;
  left: 0px;
  z-index: 1000;
}

.user-menu {
  -webkit-backdrop-filter: blur(4px);
  /* Pro Safari */

  backdrop-filter: blur(4px);
}

.v-avatar {
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: white;
}

/* Nastavení maximální šířky pro mobilní zařízení */
@media (max-width: 600px) {
  .v-menu__content {
    max-width: 150px !important;
    /* Menu bude užší na menších zařízeních */
  }
}

/* Nastavení větší šířky pro větší zařízení */
@media (min-width: 600px) {
  .v-menu__content {
    max-width: 200px !important;
    /* Menu bude širší na větších zařízeních */
  }
}

.menu-separator {
  margin-left: 8px;
  margin-right: 8px;

  &--dark {
    color: rgba(255, 255, 255, 0.4);
  }
}

.menu-btn {
  position: relative;
  transition: color 0.2s ease;
}

.menu-btn--active {
  color: #00D4FF !important;
  border-bottom: 2px solid #00D4FF !important;
  border-radius: 0 !important;
}

/* Overlay pro zavření draweru */
.drawer-overlay {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1999;
}

/* Drawer začíná pod MainMenu (50px) */
.drawer-below-menu {
  top: 50px !important;
  height: calc(100% - 50px) !important;
  z-index: 2000 !important;
}
</style>

<style>
/* Globální styl pro drawer - nescoped aby fungovalo */
.drawer-below-menu.v-navigation-drawer {
  top: 50px !important;
  height: calc(100% - 50px) !important;
  z-index: 2000 !important;
}

/* Overlay také posunout pod menu */
.v-navigation-drawer__scrim {
  top: 50px !important;
}
</style>